import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Bell, CheckCheck } from "lucide-react";
import { useI18n } from "@/context/I18nContext";

interface Notif {
  id: string;
  category: string;
  title: string;
  body: string;
  link?: string | null;
  read: boolean;
  created_at?: string;
}

const catColor: Record<string, string> = {
  security: "bg-amber-500",
  support: "bg-rose-500",
  schedule: "bg-teal-500",
  learning: "bg-indigo-500",
  reconciliation: "bg-orange-500",
  task: "bg-sky-500",
  system: "bg-slate-400",
};

export const NotificationBell: React.FC<{ dark?: boolean }> = ({ dark }) => {
  const [open, setOpen] = useState(false);
  const [count, setCount] = useState(0);
  const [items, setItems] = useState<Notif[]>([]);
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { t } = useI18n();

  const loadCount = () =>
    api.get("/notifications/unread-count").then((r) => setCount(r.data.unread_count)).catch(() => {});
  const loadList = () =>
    api.get("/notifications", { params: { limit: 12 } })
      .then((r) => { setItems(r.data.notifications); setCount(r.data.unread_count); })
      .catch(() => {});

  useEffect(() => {
    loadCount();
    const t = setInterval(loadCount, 30000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (open) loadList();
  }, [open]);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const markRead = async (n: Notif) => {
    if (!n.read) { await api.post(`/notifications/${n.id}/read`).catch(() => {}); }
    setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
    setCount((c) => Math.max(0, c - (n.read ? 0 : 1)));
    if (n.link) { setOpen(false); navigate(n.link); }
  };

  const markAll = async () => {
    await api.post("/notifications/read-all").catch(() => {});
    setItems((prev) => prev.map((x) => ({ ...x, read: true })));
    setCount(0);
  };

  return (
    (<div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="notification-bell-button"
        aria-label={t("common.notifications")}
        className={`relative grid h-9 w-9 place-items-center rounded-md transition-colors ${
          dark ? "text-sidebar-foreground/80 hover:bg-white/10 hover:text-white" : "text-muted-foreground hover:bg-secondary"
        }`}
      >
        <Bell size={18} />
        {count > 0 && (
          <span
            data-testid="notification-badge"
            className="absolute -right-0.5 -top-0.5 grid min-w-[16px] place-items-center rounded-full bg-brand px-1 text-[10px] font-bold leading-4 text-white"
          >
            {count > 99 ? "99+" : count}
          </span>
        )}
      </button>
      {open && (
        <div
          data-testid="notification-dropdown"
          className="absolute right-0 z-50 mt-2 w-80 overflow-hidden rounded-lg border border-border bg-card shadow-xl"
        >
          <div className="flex items-center justify-between border-b border-border px-3 py-2">
            <span className="text-sm font-semibold text-foreground">{t("Notifications")}</span>
            <button
              onClick={markAll}
              data-testid="notification-markall"
              className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
            >
              <CheckCheck size={13} />{" "}{t("Mark all read")}</button>
          </div>
          <div className="max-h-96 overflow-y-auto">
            {items.length === 0 ? (
              <div className="px-3 py-8 text-center text-sm text-muted-foreground" data-testid="notifications-empty">{t("You're all caught up.")}</div>
            ) : (
              items.map((n) => (
                <button
                  key={n.id}
                  onClick={() => markRead(n)}
                  data-testid="notification-item"
                  className={`flex w-full items-start gap-2.5 border-b border-border/60 px-3 py-2.5 text-left transition-colors hover:bg-secondary ${
                    n.read ? "opacity-60" : ""
                  }`}
                >
                  <span className={`mt-1 h-2 w-2 shrink-0 rounded-full ${catColor[n.category] || "bg-slate-400"}`} />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium text-foreground">{n.title}</span>
                    <span className="block text-xs text-muted-foreground line-clamp-2">{n.body}</span>
                  </span>
                  {!n.read && <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-brand" />}
                </button>
              ))
            )}
          </div>
          <button
            onClick={() => { setOpen(false); navigate("/notifications"); }}
            data-testid="notification-viewall"
            className="w-full border-t border-border py-2 text-center text-xs font-medium text-brand hover:bg-secondary"
          >{t("View all & settings")}</button>
        </div>
      )}
    </div>)
  );
};
